import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";

import {
  addOrIncrementCartItem,
  normalizeScanCode,
  resolveSingleProductFromScan,
} from "../core/scanToCart.core";
import { Cart } from "../types/cart";
import useCart from "./services/useCart.hook";

export default function useBarcodeScanner() {
  const { push, pathname, locale } = useRouter();
  const { addItem, updateItem, getProductWarehouseId } = useCart();
  const queryClient = useQueryClient();

  const codeRef = useRef("");
  const isReadingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const pathnameRef = useRef(pathname);
  const addItemRef = useRef(addItem);
  const updateItemRef = useRef(updateItem);
  const getProductWarehouseIdRef = useRef(getProductWarehouseId);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    addItemRef.current = addItem;
    updateItemRef.current = updateItem;
    getProductWarehouseIdRef.current = getProductWarehouseId;
  }, [addItem, updateItem, getProductWarehouseId]);

  const processCode = useCallback(
    async (code: string) => {
      const normalized = normalizeScanCode(code.trim());
      if (!normalized) return;

      const product = await resolveSingleProductFromScan(normalized);

      if (pathnameRef.current === "/") {
        if (product) {
          const cart = queryClient.getQueryData<Cart>(["PosCart", locale]);
          addOrIncrementCartItem({
            product,
            cart,
            addItem: addItemRef.current,
            updateItem: updateItemRef.current,
            getProductWarehouseId: getProductWarehouseIdRef.current,
            queryClient,
          });
        } else {
          push(`?search=${normalized.replace(/\+/g, "%2B")}`);
        }
        return;
      }

      if (product) {
        push("/product/" + product.id);
      }
    },
    [push, queryClient, locale]
  );

  useEffect(() => {
    function handleKeyPress(e: KeyboardEvent) {
      if (e.key !== "Enter") {
        codeRef.current += e.key;
      } else {
        if (codeRef.current.trim()) {
          if (pathnameRef.current === "/") {
            e.preventDefault();
          }
          processCode(codeRef.current);
        }
        codeRef.current = "";
      }

      if (!isReadingRef.current) {
        isReadingRef.current = true;
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          codeRef.current = "";
          isReadingRef.current = false;
        }, 2000);
      }
    }

    document.addEventListener("keypress", handleKeyPress);

    return () => {
      document.removeEventListener("keypress", handleKeyPress);
      clearTimeout(timeoutRef.current);
    };
  }, [processCode]);
}
